// src/components/TicketMessageForm.jsx
import React, { useState, useRef } from "react";
import { TicketsAPI } from "../api/tickets";
import { HelpdeskRecipesAPI } from "../api/helpdeskRecipes";
import RunRecipeModal from "./RunRecipeModal";

import PublicMessageEditor from "./tiptap/PublicMessageEditor";
import InternalNoteEditor from "./tiptap/InternalNoteEditor";

export default function TicketMessageForm({
  ticketId,
  authorId,
  visibility = "public",
  triage = false,
  onMessageAdded,
  onRecipeCompleted,
  users = [],              // NEW: users list for mentions
}) {
  const [publicJSON, setPublicJSON] = useState(null);
  const [internalJSON, setInternalJSON] = useState(null);
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [showRecipeModal, setShowRecipeModal] = useState(false);
  const [recipeStatus, setRecipeStatus] = useState("");
  const [editorKey, setEditorKey] = useState(0); // forces TipTap reset
  const fileInputRef = useRef(null);
  const messageSavedForRecipeRef = useRef(false);

  const isPublic = visibility === "public";

  const handleFileChange = (e) => {
    setFiles(Array.from(e.target.files || []));
  };

  const resetForm = () => {
    setPublicJSON("");
    setInternalJSON(null);
    setFiles([]);
    setEditorKey((k) => k + 1);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const hasContent = () => isPublic ? Boolean(publicJSON || files.length) : Boolean(internalJSON);

  const sendMessage = async (overrideRecipeId = null) => {
    let payload;
    if (isPublic && files.length > 0) {
      const formData = new FormData();
      formData.append("body", JSON.stringify(publicJSON));
      formData.append("visibility", "public");
      if(triage) formData.append("isClarification", "true");
      formData.append("author_id", String(authorId));
      if (overrideRecipeId) {
        formData.append("overrideRecipeId", overrideRecipeId);
      }
      files.forEach((file) => formData.append("attachments", file, file.name));
      payload = formData;
    } else {
        payload = {
          body: isPublic ? publicJSON : internalJSON,
          visibility,
          author_id: authorId,
          overrideRecipeId,
          isClarification: triage && isPublic,
        };
    }
    await TicketsAPI.addMessage(ticketId, payload);
    resetForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!hasContent() || submitting) return;
    setSubmitting(true);
    setError(null);
    setRecipeStatus("");
    try {
      await sendMessage();
      if (onMessageAdded) await onMessageAdded();
    } catch (err) {
      console.error(err);
      setError(err.message || "Failed to add message");
    } finally {
      setSubmitting(false);
    }
  };

  const sendAndRunRecipe = async (recipeId) => {
    if (!hasContent()) throw new Error("Write a message or note first.");
    setSubmitting(true);
    setError(null);
    setRecipeStatus("");
    // A retry after recipe failure must NOT send the message a second time.
    if (!messageSavedForRecipeRef.current) {
      try {
        await sendMessage(recipeId);
        messageSavedForRecipeRef.current = true;
      } catch (err) {
        setSubmitting(false);
        throw new Error(`Message was not sent: ${err.message || "Unknown error"}`);
      }
    }
    try {
      await HelpdeskRecipesAPI.runForTicket(ticketId, recipeId);
      setRecipeStatus("Message saved and recipe completed.");
    } catch (err) {
      setRecipeStatus("Message saved, but the recipe failed. Do not resend the message. Check the ticket before retrying the recipe.");
      setSubmitting(false);
      throw new Error(`Message saved, but recipe failed: ${err.message || "Unknown error"}`);
    }
    messageSavedForRecipeRef.current = false;
    setSubmitting(false);
    try {
      if (onRecipeCompleted) await onRecipeCompleted();
      else if (onMessageAdded) await onMessageAdded();
    } catch (err) {
      console.error("Refresh after recipe failed", err);
      setRecipeStatus("Message saved and recipe completed, but ticket refresh failed. Reload the page.");
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-2">
      {triage && isPublic && <p className="text-sm mb-2">This is a clarification request. Sending it sets Waiting for User; SLA timing remains stopped until the ticket is routed.</p>}
      {isPublic && (
        <PublicMessageEditor
          key={editorKey}
          onChange={(json) => setPublicJSON(json)}
        />
      )}

      {!isPublic && (
        <InternalNoteEditor
          key={editorKey}
          users={users}                  // NEW: pass users into internal editor
          onChange={(json) => setInternalJSON(json)}
        />
      )}

      {isPublic && (
        <div className="flex items-center justify-between mt-2">
          <div>
            <input
              type="file"
              multiple
              onChange={handleFileChange}
              className="text-xs"
              ref={fileInputRef}
            />
            {files.length > 0 && (
              <p className="text-xs text-gray-600 mt-1">
                {files.length} file{files.length !== 1 ? "s" : ""} selected
              </p>
            )}
          </div>

          <div className="flex flex-wrap gap-2 justify-end">
          <button type="button" disabled={submitting || !hasContent()} className="rounded border border-[var(--nj-border)] bg-[var(--nj-surface-secondary)] px-3 py-1 text-sm text-[var(--nj-text)] disabled:opacity-50" onClick={() => { messageSavedForRecipeRef.current = false; setShowRecipeModal(true); }}>Send &amp; Run Recipe</button>
          <button
            type="submit"
            disabled={submitting}
            className="bg-blue-500 text-white px-3 py-1 rounded text-sm disabled:opacity-50"
          >
            {submitting ? "Sending…" : "Send message"}
          </button>
          </div>
        </div>
      )}

      {!isPublic && (
        <div className="flex justify-end gap-2 flex-wrap">
          <button type="button" disabled={submitting || !hasContent()} className="rounded border border-[var(--nj-border)] bg-[var(--nj-surface-secondary)] px-3 py-1 text-sm text-[var(--nj-text)] disabled:opacity-50" onClick={() => { messageSavedForRecipeRef.current = false; setShowRecipeModal(true); }}>Add Note &amp; Run Recipe</button>
          <button
            type="submit"
            disabled={submitting}
            className="bg-blue-500 text-white px-3 py-1 rounded text-sm disabled:opacity-50"
          >
            {submitting ? "Saving…" : "Add internal note"}
          </button>
        </div>
      )}

      {error && <p className="text-xs text-red-500">{error}</p>}
      {recipeStatus && <p role="status" className="text-xs text-[var(--nj-text)]">{recipeStatus}</p>}
      <RunRecipeModal open={showRecipeModal} ticketId={ticketId} title={isPublic ? "Send message and run recipe" : "Add note and run recipe"} onClose={() => { setShowRecipeModal(false); if (messageSavedForRecipeRef.current) setRecipeStatus("Message saved. Recipe was not completed; check the ticket before running it separately."); messageSavedForRecipeRef.current = false; }} onRun={sendAndRunRecipe} />
    </form>
  );
}
